import React, { useState, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from "@material-ui/core/styles";
import Badge from '@material-ui/core/Badge';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { getBadgeContent, isComicOwned } from './utils';

const useStyles = makeStyles(theme => ({
    webPlant: {
        padding: '10px 10px 5px 5px',
        display: 'inline-flex'
    },
    separator: {
        paddingTop: '10px'
    },
    title: {
        fontSize: '1.5em',
        fontWeight: 'bold',
        padding: '10px',
        textAlign: 'center'
    },
    filterContainer: {
        padding: '10px',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '10px'
    },
    formControl: {
        minWidth: 200
    },
    clickable: {
        cursor: 'pointer'
    },
    comicContainer: {
        position: 'relative'
    },
    toggleButton: {
        position: 'absolute',
        top: 0,
        right: 0,
        zIndex: 1,
        color: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }
    },
    owned: {
        color: '#4caf50'
    }
}));

const MissingIssues = (props) => {
    const classes = useStyles();
    const [selectedHero, setSelectedHero] = useState('all');

    // Get unique heroes from missing comics
    const uniqueHeroes = useMemo(() => {
        const heroes = new Set();
        props.missingComics.forEach(comic => {
            if (comic.heroName) {
                heroes.add(comic.heroName);
            }
        });
        return Array.from(heroes).sort();
    }, [props.missingComics]);

    // Filter comics by selected hero
    const filteredComics = useMemo(() => {
        if (selectedHero === 'all') {
            return props.missingComics;
        }
        return props.missingComics.filter(comic => comic.heroName === selectedHero);
    }, [props.missingComics, selectedHero]);

    const handleHeroChange = (event) => {
        setSelectedHero(event.target.value);
    };

    return (
        <div>
            <div className={classes.title}>Gap Issues</div>
            <div className={classes.filterContainer}>
                <FormControl className={classes.formControl}>
                    <InputLabel id="hero-filter-label">Filter by Hero</InputLabel>
                    <Select
                        labelId="hero-filter-label"
                        id="hero-filter"
                        value={selectedHero}
                        onChange={handleHeroChange}
                    >
                        <MenuItem value="all">All Heroes</MenuItem>
                        {uniqueHeroes.map(hero => (
                            <MenuItem key={hero} value={hero}>{hero}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            <Grid container className={classes.separator}>
                {filteredComics
                    .sort((a, b) => {
                        // Sort by onsale date descending (newest first)
                        const dateA = a.dates && a.dates.find(d => d.type === 'onsaleDate');
                        const dateB = b.dates && b.dates.find(d => d.type === 'onsaleDate');
                        const timeA = dateA ? new Date(dateA.date).getTime() : 0;
                        const timeB = dateB ? new Date(dateB.date).getTime() : 0;
                        return timeB - timeA;
                    })
                    .map(comic => <MissingComic 
                        key={comic.id} 
                        {...comic} 
                        cards={props.cards}
                        setCurrentCard={props.setCurrentCard}
                        setCurrentSeries={props.setCurrentSeries}
                        updateIGotStatus={props.updateIGotStatus}
                        comicsIGot={props.comicsIGot}
                    />)}
            </Grid>
        </div>
    );
};

const MissingComic = withRouter((props) => {
    const classes = useStyles();
    const onsaleDate = props.dates && props.dates.find(d => d.type === 'onsaleDate');
    const formattedDate = onsaleDate ? new Date(onsaleDate.date).toLocaleDateString() : 'Unknown';
    
    // Check if this comic is owned
    const isOwned = props.comicsIGot && isComicOwned(props, props.comicsIGot);
    
    const handleClick = () => {
        // Find the hero card by name
        const heroCard = props.cards && props.cards.find(card => card.name === props.heroName);
        
        if (heroCard && props.setCurrentCard && props.setCurrentSeries) {
            // Set the current hero
            props.setCurrentCard(heroCard);
            
            // Set the current series
            if (props.seriesData) {
                props.setCurrentSeries(props.seriesData, heroCard);
            }
            
            // Navigate to the series page
            props.history.push('/series/');
        }
    };
    
    const handleToggleOwnership = (event) => {
        event.stopPropagation(); // Prevent navigation when toggling ownership
        
        if (props.updateIGotStatus && props.seriesData && props.heroData) {
            const newGotItStatus = !isOwned;
            props.updateIGotStatus(props, newGotItStatus, props.seriesData.id, props.heroData.id || 0);
        }
    };
    
    return (
        <Grid item className={classes.webPlant}>
            <div className={`cardwrapper_nohighlight ${classes.comicContainer}`}>
                {props.updateIGotStatus && (
                    <IconButton
                        size="small"
                        className={classes.toggleButton}
                        onClick={handleToggleOwnership}
                        title={isOwned ? "Mark as not owned" : "Mark as owned"}
                    >
                        {isOwned ? (
                            <CheckCircleIcon className={classes.owned} />
                        ) : (
                            <CheckCircleOutlineIcon />
                        )}
                    </IconButton>
                )}
                <Badge badgeContent={getBadgeContent(props.issueNumber, props.variantDescription)} color="primary" max={9999}>
                    <img 
                        src={props.thumbnail.path.replace('http', 'https') + '/portrait_medium.' + props.thumbnail.extension} 
                        title={props.title}
                        alt={props.title}
                        onClick={handleClick}
                        className={classes.clickable}
                    />
                </Badge>
                <div style={{ fontSize: '0.8em', textAlign: 'center' }}>
                    {formattedDate}
                </div>
            </div>
        </Grid>
    );
});

export default MissingIssues;
